import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";

export default function ConnectGmail() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const justConnected = params.get("status") === "connected";
  const connectedEmail = params.get("email");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/integrations/gmail/status");
      setStatus(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const connect = async () => {
    setError("");
    try {
      const { data } = await api.get("/integrations/gmail/connect");
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(t("app.connectGmail.disconnectConfirm"))) return;
    try {
      await api.post("/integrations/gmail/disconnect");
      await loadStatus();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="px-6 md:px-12 py-10 max-w-3xl mx-auto" data-testid="connect-gmail-page">
      <p className="label-cap">— {t("app.connectGmail.tag")}</p>
      <h1 className="font-display text-4xl md:text-5xl mt-3" data-testid="connect-gmail-title">
        {t("app.connectGmail.title")}
      </h1>
      <p className="font-display-italic text-lg mt-3 text-[var(--ink-soft)] max-w-2xl">
        {t("app.connectGmail.sub")}
      </p>

      {justConnected && (
        <div
          className="mt-8 p-6 bg-[var(--surface)] border-l-2 border-[var(--oxblood)]"
          data-testid="connect-gmail-success"
        >
          <p className="font-display-italic text-lg">
            {t("app.connectGmail.connectedFlash").replace("{email}", connectedEmail || "")}
          </p>
        </div>
      )}

      <section className="mt-12">
        {loading ? (
          <p className="label-cap text-[var(--ink-faint)]">…</p>
        ) : status?.connected ? (
          <div className="space-y-6" data-testid="connect-gmail-connected">
            <div className="p-8 bg-[var(--bg)] border border-[var(--border)]">
              <p className="label-cap text-[var(--oxblood)]">— {t("app.connectGmail.active")}</p>
              <p className="font-display text-2xl mt-3">{status.email}</p>
              <p className="text-sm text-[var(--ink-soft)] mt-2">
                {t("app.connectGmail.connectedAt").replace(
                  "{when}",
                  (status.connected_at || "").slice(0, 10),
                )}
              </p>
            </div>
            <BriefingPanel />
            <WrapupPanel />
            <TriagePanel />
            <button
              onClick={disconnect}
              className="btn-ghost text-[var(--oxblood)]"
              data-testid="connect-gmail-disconnect"
            >
              {t("app.connectGmail.disconnect")}
            </button>
          </div>
        ) : (
          <div className="space-y-8" data-testid="connect-gmail-cta">
            <ul className="space-y-4 text-[15px]">
              {t("app.connectGmail.promises").map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-[0.45rem] inline-block w-3 h-px bg-[var(--oxblood)]" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={connect}
              className="btn-primary"
              data-testid="connect-gmail-start"
            >
              {t("app.connectGmail.cta")}
            </button>
            <p className="text-xs text-[var(--ink-faint)] max-w-xl">
              {t("app.connectGmail.privacy")}
            </p>
          </div>
        )}
      </section>

      {error && <p className="mt-6 text-sm text-[var(--oxblood)]">{error}</p>}
    </div>
  );
}

function BriefingPanel() {
  const { t } = useLang();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState("");
  const [err, setErr] = useState("");

  const loadPreview = async () => {
    setLoading(true);
    setErr("");
    setFlash("");
    try {
      const { data } = await api.get("/integrations/gmail/briefing/preview");
      setPreview(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const sendBriefing = async () => {
    setSending(true);
    setErr("");
    setFlash("");
    try {
      const { data } = await api.post("/integrations/gmail/briefing/send");
      if (data.skipped && data.reason === "no_whatsapp_phone") {
        setErr(t("app.connectGmail.briefingNoWhatsapp"));
      } else if (data.ok) {
        setFlash(t("app.connectGmail.briefingSent"));
      } else {
        setErr(data.reason || "send_failed");
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 bg-[var(--bg)] border border-[var(--border)]" data-testid="gmail-briefing-panel">
      <div className="flex items-center justify-between gap-4">
        <p className="label-cap">— {t("app.connectGmail.briefingTitle")}</p>
        <div className="flex gap-2">
          <button
            onClick={loadPreview}
            disabled={loading}
            className="btn-ghost text-sm"
            data-testid="briefing-preview-btn"
          >
            {loading ? "…" : t("app.connectGmail.briefingPreview")}
          </button>
          <button
            onClick={sendBriefing}
            disabled={sending}
            className="btn-primary text-sm"
            data-testid="briefing-send-btn"
          >
            {sending ? t("app.connectGmail.briefingSending") : t("app.connectGmail.briefingSend")}
          </button>
        </div>
      </div>
      {preview && (
        <div className="mt-4" data-testid="briefing-preview-output">
          {preview.text ? (
            <pre className="font-display-italic text-base text-[var(--ink)] whitespace-pre-wrap leading-relaxed">
              {preview.text}
            </pre>
          ) : (
            <p className="font-display-italic text-sm text-[var(--ink-soft)]" data-testid="briefing-empty">
              {t("app.connectGmail.briefingEmpty")}
            </p>
          )}
        </div>
      )}
      {flash && <p className="mt-3 text-sm text-[var(--oxblood)]" data-testid="briefing-flash">{flash}</p>}
      {err && <p className="mt-3 text-sm text-[var(--oxblood)]" data-testid="briefing-error">{err}</p>}
    </div>
  );
}

function WrapupPanel() {
  const { t } = useLang();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState("");
  const [err, setErr] = useState("");

  const loadPreview = async () => {
    setLoading(true); setErr(""); setFlash("");
    try {
      const { data } = await api.get("/integrations/gmail/wrapup/preview");
      setPreview(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const sendWrapup = async () => {
    setSending(true); setErr(""); setFlash("");
    try {
      const { data } = await api.post("/integrations/gmail/wrapup/send");
      if (data.skipped && data.reason === "no_whatsapp_phone") {
        setErr(t("app.connectGmail.briefingNoWhatsapp"));
      } else if (data.skipped && data.reason === "no_activity_today") {
        setFlash(t("app.connectGmail.wrapupNoActivity"));
      } else if (data.ok) {
        setFlash(t("app.connectGmail.wrapupSent"));
      } else {
        setErr(data.reason || "send_failed");
      }
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSending(false); }
  };

  return (
    <div className="p-6 bg-[var(--bg)] border border-[var(--border)]" data-testid="gmail-wrapup-panel">
      <div className="flex items-center justify-between gap-4">
        <p className="label-cap">— {t("app.connectGmail.wrapupTitle")}</p>
        <div className="flex gap-2">
          <button onClick={loadPreview} disabled={loading} className="btn-ghost text-sm" data-testid="wrapup-preview-btn">
            {loading ? "…" : t("app.connectGmail.wrapupPreview")}
          </button>
          <button onClick={sendWrapup} disabled={sending} className="btn-primary text-sm" data-testid="wrapup-send-btn">
            {sending ? t("app.connectGmail.briefingSending") : t("app.connectGmail.wrapupSend")}
          </button>
        </div>
      </div>
      {preview && (
        <div className="mt-4" data-testid="wrapup-preview-output">
          {preview.text ? (
            <>
              <pre className="font-display-italic text-base text-[var(--ink)] whitespace-pre-wrap leading-relaxed">
                {preview.text}
              </pre>
              <p className="text-xs text-[var(--ink-faint)] mt-3" data-testid="wrapup-counters">
                {preview.received} recebidos · {preview.sent} enviados · {preview.archived} arquivados · {preview.pending_open} pendentes
              </p>
            </>
          ) : (
            <p className="font-display-italic text-sm text-[var(--ink-soft)]" data-testid="wrapup-empty">
              {t("app.connectGmail.wrapupNoActivity")}
            </p>
          )}
        </div>
      )}
      {flash && <p className="mt-3 text-sm text-[var(--oxblood)]" data-testid="wrapup-flash">{flash}</p>}
      {err && <p className="mt-3 text-sm text-[var(--oxblood)]" data-testid="wrapup-error">{err}</p>}
    </div>
  );
}

function TriagePanel() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [running, setRunning] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  const load = useCallback(async () => {
    try {
      const [m, s] = await Promise.all([
        api.get("/integrations/gmail/messages?limit=20"),
        api.get("/integrations/gmail/summary"),
      ]);
      setItems(m.data.items || []);
      setSummary(s.data);
    } catch (err) {
      setLoadErr(formatApiError(err.response?.data?.detail) || err.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runTriage = async () => {
    setRunning(true);
    setLoadErr("");
    try {
      await api.post("/integrations/gmail/triage?limit=20");
      await load();
    } catch (err) {
      setLoadErr(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRunning(false);
    }
  };

  const urgencyClass = (u) => ({
    urgent: "text-[var(--oxblood)]",
    needs_reply: "text-[var(--ink)]",
    informational: "text-[var(--ink-soft)]",
    noise: "text-[var(--ink-faint)]",
  })[u] || "text-[var(--ink-soft)]";

  const updateItem = (id, patch) =>
    setItems((arr) => arr.map((m) => (m.id === id ? { ...m, ...patch } : m)));

  const removeItem = (id) => setItems((arr) => arr.filter((m) => m.id !== id));

  return (
    <div className="space-y-6" data-testid="gmail-triage-panel">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="label-cap">— {t("app.connectGmail.triageTitle")}</p>
          {summary && (
            <p className="font-display-italic text-lg mt-2 text-[var(--ink-soft)]">
              {summary.urgent > 0 && <span className="text-[var(--oxblood)]" data-testid="triage-urgent">{summary.urgent} urgente · </span>}
              {summary.needs_reply > 0 && <span data-testid="triage-needs-reply">{summary.needs_reply} a responder · </span>}
              <span data-testid="triage-informational">{summary.informational} informativos</span>
            </p>
          )}
        </div>
        <button
          onClick={runTriage}
          disabled={running}
          className="btn-primary"
          data-testid="gmail-triage-run"
        >
          {running ? t("app.connectGmail.triaging") : t("app.connectGmail.triageBtn")}
        </button>
      </div>

      {loadErr && <p className="text-sm text-[var(--oxblood)]">{loadErr}</p>}

      {items.length === 0 ? (
        <p className="font-display-italic text-base text-[var(--ink-soft)]" data-testid="gmail-triage-empty">
          {t("app.connectGmail.triageEmpty")}
        </p>
      ) : (
        <ul className="divide-y divide-[var(--border)]" data-testid="gmail-triage-list">
          {items.map((m) => (
            <EmailRow
              key={m.id}
              email={m}
              urgencyClass={urgencyClass}
              onUpdate={(patch) => updateItem(m.id, patch)}
              onRemove={() => removeItem(m.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function EmailRow({ email, urgencyClass, onUpdate, onRemove }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(email.draft_reply || "");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [err, setErr] = useState("");

  const isSent = !!email.sent_at;
  const isArchived = !!email.archived;

  const generate = async () => {
    setDrafting(true);
    setErr("");
    try {
      const { data } = await api.post(`/integrations/gmail/messages/${email.id}/draft`);
      setDraft(data.draft_reply || "");
      setOpen(true);
      onUpdate({ draft_reply: data.draft_reply });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDrafting(false);
    }
  };

  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    setErr("");
    try {
      // persist any edits first
      await api.patch(`/integrations/gmail/messages/${email.id}/draft`, { draft_reply: draft });
      const { data } = await api.post(`/integrations/gmail/messages/${email.id}/send`);
      onUpdate({ sent_at: data.sent_at, archived: true });
      setOpen(false);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const archive = async () => {
    setArchiving(true);
    setErr("");
    try {
      await api.post(`/integrations/gmail/messages/${email.id}/archive`);
      onRemove();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      setArchiving(false);
    }
  };

  return (
    <li className="py-4" data-testid={`email-row-${email.id}`}>
      <div className="flex items-baseline justify-between gap-4">
        <p className={`label-cap ${urgencyClass(email.urgency)}`}>— {email.urgency}</p>
        <p className="label-cap text-[var(--ink-faint)]">{(email.received_at || "").slice(0, 10)}</p>
      </div>
      <p className="font-display text-lg mt-2 leading-snug">{email.subject || "(sem assunto)"}</p>
      <p className="text-sm text-[var(--ink-soft)] mt-1">{email.from_name || email.from_email}</p>
      {email.summary_pt && (
        <p className="font-display-italic text-sm mt-2 text-[var(--ink)]">{email.summary_pt}</p>
      )}

      {isSent ? (
        <p className="mt-3 text-sm text-[var(--oxblood)]" data-testid={`email-sent-${email.id}`}>
          {t("app.connectGmail.sentLabel")} · {(email.sent_at || "").slice(11, 16)}
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {!open && (
            <button
              onClick={generate}
              disabled={drafting || isArchived}
              className="btn-ghost text-sm"
              data-testid={`email-draft-btn-${email.id}`}
            >
              {drafting ? t("app.connectGmail.drafting") : t("app.connectGmail.draftBtn")}
            </button>
          )}
          <button
            onClick={archive}
            disabled={archiving || isArchived}
            className="btn-ghost text-sm text-[var(--ink-soft)]"
            data-testid={`email-archive-btn-${email.id}`}
          >
            {archiving ? t("app.connectGmail.archiving") : t("app.connectGmail.archiveBtn")}
          </button>
        </div>
      )}

      {open && !isSent && (
        <div className="mt-4 p-4 bg-[var(--surface)] border-l-2 border-[var(--oxblood)]" data-testid={`email-draft-${email.id}`}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={t("app.connectGmail.draftPlaceholder")}
            rows={Math.max(6, Math.min(16, (draft || "").split("\n").length + 2))}
            className="w-full bg-transparent border border-[var(--border)] p-3 text-[15px] leading-relaxed font-mono"
            data-testid={`email-draft-textarea-${email.id}`}
          />
          <div className="mt-3 flex gap-2">
            <button
              onClick={send}
              disabled={sending || !draft.trim()}
              className="btn-primary text-sm"
              data-testid={`email-send-btn-${email.id}`}
            >
              {sending ? t("app.connectGmail.sending") : t("app.connectGmail.sendBtn")}
            </button>
            <button
              onClick={() => setOpen(false)}
              disabled={sending}
              className="btn-ghost text-sm"
              data-testid={`email-cancel-btn-${email.id}`}
            >
              {t("app.connectGmail.cancelBtn")}
            </button>
          </div>
        </div>
      )}

      {err && <p className="mt-2 text-sm text-[var(--oxblood)]" data-testid={`email-error-${email.id}`}>{err}</p>}
    </li>
  );
}
